import React from 'react'
import { isNil, toString } from 'lodash'

import {
  AddExperimentRequest,
  AdoptionDashboardDataResponse,
  AdoptionDashboardFiltersResponse,
  ApplicationAnalyticsResponse,
  AuthInitialConfig,
  AvailableExperimentsResponse,
  ChatMessage,
  Conversation,
  ConversationRequest,
  CosmosDBHealth,
  CosmosDBStatus,
  DismissMicroLearningRequest,
  EditMicroLearningPayload,
  EditMicroLearningResponse,
  ExperimentFiltersOptions,
  ExperimentLinkTarget,
  ExperimentLogsParams,
  ExperimentLogsRangesResponse,
  ExperimentLogsResponse,
  ExperimentMutationResult,
  ExperimentsListProps,
  ExperimentsListResponse,
  FilterOptions,
  FreezeYTDResponse,
  FrontendSettings,
  MicroLearningDetailResponse,
  MicroLearningExportFormat,
  MicroLearningFilterOptions,
  MicroLearningHistoryEntry,
  MicroLearningsListParams,
  MicroLearningsListResponse,
  NotificationsResponse,
  PendingRequestsResponse,
  PeriodItem,
  RequestHistoryFilters,
  RequestHistoryResponse,
  RoleChangeRequestOptions,
  RoleChangeRequestPayload,
  RoleChangeRequestResponse,
  SummaryByOUFiltersResponse,
  SummaryByOUResponse,
  SystemPrompt,
  UnreadNotificationsCountResponse,
  User,
  UserCreatePayload,
  UserDeleteResponse,
  UserFilterOptions,
  UserHistoryParams,
  UserHistoryResponse,
  UserListParams,
  UserListResponse,
  UserResponse,
  UserRole,
  UserUpdatePayload,
  ViewEditAdoptionStatusFiltersResponse,
  ViewEditAdoptionStatusListResponse,
  ViewEditAdoptionStatusRow,
} from './models'
import { getIdToken, getJwtToken } from './token'

// App-Secret storage - populated by fetchAppSecret after authentication
let appSecretValue: string | null = null

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = ['/api/auth_config']

/**
 * Wrapper around fetch that automatically handles 401 errors by refreshing the token
 * and retrying the request using the refreshToken function from token.ts
 */
async function fetchWithTokenRefresh(url: string, options?: RequestInit, retryCount = 0): Promise<Response> {
  // Skip token refresh for public endpoints
  const pathWithoutQuery = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0]
  const pathNormalized = pathWithoutQuery.replace(/\/+$/, '')
  const isPublicEndpoint = PUBLIC_ENDPOINTS.some(ep => ep.replace(/\/+$/, '') === pathNormalized)

  // Make the initial request
  let response = await fetch(url, options)

  if (isPublicEndpoint || response.status !== 401 || retryCount > 1) {
    return response
  }

  try {
    // Force refresh the token - this will call refreshToken internally via getJwtToken(true)
    const newToken = await getJwtToken(true)

    if (!newToken) {
      return response
    }

    // Clone the options to avoid mutating the original
    const existingHeaders = options?.headers
    let retryHeaders: HeadersInit

    if (existingHeaders instanceof Headers) {
      // If headers is a Headers object, create a new one and copy values
      retryHeaders = new Headers(existingHeaders)
      retryHeaders.set('Authorization', 'Bearer ' + newToken.idToken)
      retryHeaders.set('Access-Token', newToken.accessToken || '')
    } else if (Array.isArray(existingHeaders)) {
      // If headers is an array of tuples, convert to object and update
      const headersObj: Record<string, string> = {}
      existingHeaders.forEach(([key, value]) => {
        headersObj[key] = value
      })
      headersObj['Authorization'] = 'Bearer ' + newToken.idToken
      headersObj['Access-Token'] = newToken.accessToken || ''
      retryHeaders = headersObj
    } else {
      // If headers is an object or undefined, merge and update
      retryHeaders = {
        ...(existingHeaders as Record<string, string>),
        Authorization: 'Bearer ' + newToken.idToken,
        'Access-Token': newToken.accessToken || '',
      }
    }
    const retryOptions: RequestInit = {
      ...options,
      headers: retryHeaders,
    }
    // Preserve the body if it exists
    if (options?.body) {
      retryOptions.body = options.body
    }
    // Retry the request with the new token (increment retryCount to prevent infinite loops)
    response = await fetchWithTokenRefresh(url, retryOptions, retryCount + 1)
  } catch (error) {
    // If token refresh fails, return the original 401 response
    console.error('Failed to refresh token:', error)
  }
  return response
}

// Initialize app secret from localStorage on page load
const initAppSecretFromStorage = () => {
  const storedSecret = localStorage.getItem('app_secret')
  if (storedSecret) {
    appSecretValue = storedSecret
  }
}
initAppSecretFromStorage()

// Helper function to get headers with App-Secret
const getApiHeaders = async (additionalHeaders: Record<string, string> = {}): Promise<Record<string, string>> => {
  const headers: Record<string, string> = { ...additionalHeaders }

  // Add App-Secret from the stored value (fetched after authentication)
  if (appSecretValue) {
    headers['App-Secret'] = appSecretValue
  }

  return headers
}

/**
 * Shared fetch utility that handles auth headers, JSON parsing, and error logging.
 * Returns `null` when the request fails or the response is not OK.
 */
const apiFetch = async <T>(
  url: string,
  options?: { method?: string; body?: unknown; errorMessage?: string },
): Promise<T | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const fetchOptions: RequestInit = {
    method: options?.method ?? 'GET',
    headers,
  }
  if (options?.body !== undefined) {
    fetchOptions.body = JSON.stringify(options.body)
  }

  return await fetch(url, fetchOptions)
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return (await res.json()) as T
    })
    .catch(_err => {
      console.error(options?.errorMessage ?? 'There was an issue fetching your data.')
      return null
    })
}

export const authInitialConfig = async (): Promise<AuthInitialConfig | null> => {
  const response = await fetchWithTokenRefresh('/api/auth_config', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(async res => {
      if (!res.ok) {
        console.error('Failed to fetch auth config:', res.status)
        return null
      }
      const auth = await res.json()
      return { auth } as AuthInitialConfig
    })
    .catch(_err => {
      console.error('Error fetching auth initial config:', _err)
      return null
    })

  return response
}

export const fetchAppSecret = async (): Promise<boolean> => {
  const jwt = await getJwtToken()

  try {
    const response = await fetchWithTokenRefresh('/api/app_secret', {
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + jwt?.idToken,
        'Access-Token': jwt?.accessToken || '',
      },
    })

    if (!response.ok) {
      console.error('Failed to fetch app secret:', response.status)
      return false
    }

    const data = await response.json()

    if (data.enabled && data.header_value) {
      appSecretValue = data.header_value
      localStorage.setItem('app_secret', data.header_value)
    } else if (!data.enabled) {
      console.log('App-Secret is disabled on backend')
    }
    return true
  } catch (error) {
    console.error('Error fetching app secret:', error)
    return false
  }
}

const mapPayloadToConversation = (payload: any): Conversation => ({
  id: payload.id,
  title: payload.title,
  messages: [],
  created_at: payload.created_at,
  extended_data: payload.extended_data,
  selected_agent: payload.selected_agent,
})

export const bootstrap = async (): Promise<{
  success: boolean
  status: number
  user: User
  has_pending_role_change_request: boolean
  unread_notifications_count: number
  lookups: { ous: string[] }
}> => {
  const jwt = await getJwtToken()

  return await fetchWithTokenRefresh('/api/bootstrap', {
    method: 'POST',
    headers: {
      Authorization: 'Bearer ' + jwt?.idToken,
      'Access-Token': jwt?.accessToken || '',
    },
  }).then(async res => {
    const json = await res.json()
    return {
      success: res.ok,
      status: res.status,
      user: json.user,
      has_pending_role_change_request: json.has_pending_role_change_request,
      unread_notifications_count: json.unread_notifications_count,
      lookups: json.lookups || { ous: [] },
    }
  })
}

export const historyList = async (offset = 0, withExperiment?: boolean): Promise<Conversation[] | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  let url = `/api/history/list?offset=${offset}`
  if (!isNil(withExperiment)) {
    url += `&with_experiment=${withExperiment}`
  }

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      const payload = await res.json()
      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }

      return payload
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const historyRead = async (
  convId?: string,
  experimentId?: string,
  viewOnly?: boolean,
  title?: string,
): Promise<Conversation | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/history/read', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      experiment_id: experimentId,
      view_only: viewOnly,
      title,
    }),
    headers,
  })
    .then(async res => {
      if (!res) {
        return null
      }
      const payload = await res.json()
      const conversation: Conversation = payload.conversation
      if (payload.messages) {
        conversation.messages = payload.messages.map((msg: any) => {
          return {
            id: msg.id,
            role: msg.role,
            date: msg.created_at,
            content: msg.content,
            feedback: msg.feedback ?? undefined,
            agent_name: msg.agent_name,
            sections: msg.sections,
            message_type: msg.message_type,
          }
        })
      }

      return conversation
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })
  return response
}

export const historyGenerate = async (
  options: ConversationRequest,
  abortSignal: AbortSignal,
  shouldValidateForm: boolean,
  convId?: string,
  title?: string,
): Promise<Response> => {
  let body
  if (convId) {
    body = JSON.stringify({
      conversation_id: convId,
      messages: options.messages,
      should_validate_form: shouldValidateForm,
      title,
    })
  } else {
    body = JSON.stringify({
      messages: options.messages,
      title,
    })
  }

  const jwt = await getJwtToken()
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + jwt?.idToken,
    'Access-Token': jwt?.accessToken || '',
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/history/generate', {
    method: 'POST',
    headers,
    body: body,
    signal: abortSignal,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return new Response()
    })
  return response
}

export const historyUpdate = async (messages: ChatMessage[], convId: string): Promise<Response> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/history/update', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      messages: messages,
    }),
    headers,
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const historyDelete = async (convId: string): Promise<Response> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/history/delete', {
    method: 'DELETE',
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const historyDeleteAll = async (): Promise<Response> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/history/delete_all', {
    method: 'DELETE',
    body: JSON.stringify({}),
    headers,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const historyRename = async (convId: string, title: string): Promise<Response> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/history/rename', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      title: title,
    }),
    headers,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const historyPin = async (convId: string, pinned: boolean): Promise<Response | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/history/pin', {
    method: 'PUT',
    body: JSON.stringify({
      conversation_id: convId,
      pinned: pinned,
    }),
    headers,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue pinning/unpinning the conversation.')
      return null
    })
  return response
}

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
  const jwt = await getJwtToken()
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + jwt?.idToken,
    'Access-Token': jwt?.accessToken || '',
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/history/ensure', {
    method: 'GET',
    headers,
  })
    .then(async res => {
      const respJson = await res.json()
      let formattedResponse
      if (respJson.message) {
        formattedResponse = CosmosDBStatus.Working
      } else {
        if (res.status === 500) {
          formattedResponse = CosmosDBStatus.NotWorking
        } else if (res.status === 401) {
          formattedResponse = CosmosDBStatus.InvalidCredentials
        } else if (res.status === 422) {
          formattedResponse = respJson.error
        } else {
          formattedResponse = CosmosDBStatus.NotConfigured
        }
      }
      if (!res.ok) {
        getJwtToken(true)
        return {
          cosmosDB: false,
          status: formattedResponse,
        }
      } else {
        return {
          cosmosDB: true,
          status: formattedResponse,
        }
      }
    })
    .catch(err => {
      console.error('There was an issue fetching your data.')
      return {
        cosmosDB: false,
        status: err,
      }
    })
  return response
}

export const frontendSettings = async (): Promise<FrontendSettings | null> => {
  const jwt = await getJwtToken()
  const response = await fetchWithTokenRefresh('/api/frontend_settings', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + jwt?.idToken,
      'Access-Token': jwt?.accessToken || '',
    },
  })
    .then(async res => {
      const jsonResponse = await res.json()

      const frontendSettings = localStorage.getItem('frontend_settings')
      const jsonFrontendSettings = JSON.parse(frontendSettings ?? '{}')
      const buildVersion = jsonFrontendSettings?.build?.version

      if (buildVersion !== jsonResponse?.build?.version) {
        sessionStorage.clear()
        localStorage.clear()
      }
      localStorage.setItem('frontend_settings', JSON.stringify(jsonResponse))

      return jsonResponse
    })
    .catch(_err => {
      console.error('Error fetching frontend settings:', _err)
      return null
    })

  return response
}

export const historyMessageFeedback = async (
  messageId: string,
  feedback: { type?: string; details?: string },
): Promise<Response> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/history/message_feedback', {
    method: 'POST',
    body: JSON.stringify({
      message_id: messageId,
      message_feedback: feedback,
    }),
    headers,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue logging feedback.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const updateExperimentFormField = async (
  step: string,
  fieldName: string,
  fieldValue: any,
  convId: string,
  shouldValidateForm: boolean,
  frontendSettings?: FrontendSettings,
): Promise<Conversation | null> => {
  const jwt = await getJwtToken()
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + jwt?.idToken,
    'Access-Token': jwt?.accessToken || '',
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/experiment_form/field', {
    method: 'PUT',
    body: JSON.stringify({
      conversation_id: convId,
      step: step,
      field_name: fieldName,
      field_value: fieldValue,
      should_validate_form: shouldValidateForm,
    }),
    headers,
  })
    .then(async res => {
      if (res.ok) {
        const payload = await res.json()
        const conversation: Conversation = {
          id: payload.id,
          title: payload.title,
          messages: [],
          created_at: payload.created_at,
          extended_data: payload.extended_data,
          selected_agent: payload.selected_agent,
        }
        return conversation
      }
      if (res.status === 403) {
        const error = new Error(
          `You do not have permission to create experiments. Request the access here`,
        ) as Error & {
          reactElement?: React.ReactNode
        }
        error.reactElement = React.createElement(
          React.Fragment,
          null,
          'You do not have permission to create experiments. Request the access ',
          React.createElement(
            'span',
            {
              style: { cursor: 'pointer', textDecoration: 'underline', color: 'inherit' },
              onClick: () => {
                // Dispatch custom event to open access request dialog
                window.dispatchEvent(new CustomEvent('openAccessRequestDialog'))
              },
            },
            'here',
          ),
          '.',
        )
        throw error
      }
      const errorText = await res.text()
      throw new Error(errorText || 'There was an error updating experiment form field.')
    })
    .catch(_err => {
      throw _err
    })
  return response
}

export const addExperimentFormFile = async (
  step: string,
  fieldName: string,
  file: File,
  convId: string,
  shouldValidateForm: boolean,
): Promise<Conversation | null> => {
  const formData = new FormData()
  formData.append('conversation_id', convId)
  formData.append('step', step)
  formData.append('field_name', fieldName)
  formData.append('field_value', file)
  formData.append('should_validate_form', toString(shouldValidateForm))

  const jwt = await getJwtToken()
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + jwt?.idToken,
    'Access-Token': jwt?.accessToken || '',
    // Do not set the Content-Type header, browser will do it automatically
  })

  const response = await fetchWithTokenRefresh('/api/experiment_form/file', {
    method: 'POST',
    body: formData,
    headers,
  })

  if (!response.ok) {
    throw new Error('An error occurred while uploading the file')
  }

  const payload = await response.json()
  return mapPayloadToConversation(payload)
}

export const deleteExperimentFormFile = async (
  step: string,
  fieldName: string,
  file_id: string,
  convId: string,
  shouldValidateForm: boolean,
): Promise<Conversation | null> => {
  const jwt = await getJwtToken()
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + jwt?.idToken,
    'Access-Token': jwt?.accessToken || '',
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/experiment_form/file', {
    method: 'DELETE',
    body: JSON.stringify({
      conversation_id: convId,
      step: step,
      field_name: fieldName,
      field_value: file_id,
      should_validate_form: shouldValidateForm,
    }),
    headers,
  })

  if (!response.ok) {
    throw new Error('An error occurred while deleting the file')
  }

  const payload = await response.json()
  return mapPayloadToConversation(payload)
}

export const updateExperimentFormStep = async (
  step: string,
  convId: string,
): Promise<(Conversation & { hasFormErrors?: boolean }) | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/experiment_form/step', {
    method: 'PUT',
    body: JSON.stringify({
      conversation_id: convId,
      step: step,
    }),
    headers,
  })
    .then(async res => {
      if (res.ok) {
        const payload = await res.json()
        const conversation: Conversation = {
          id: payload.id,
          title: payload.title,
          messages: [],
          created_at: payload.created_at,
          extended_data: payload.extended_data,
          selected_agent: payload.selected_agent,
        }
        return conversation
      } else if (res.status === 422) {
        const payload = await res.json()
        const response: Conversation = {
          id: payload.id,
          title: payload.title,
          messages: [],
          created_at: payload.created_at,
          extended_data: payload.extended_data,
          selected_agent: payload.selected_agent,
        }
        return { ...response, hasFormErrors: true }
      }

      console.error('There was error an updating experiment form field.', await res.text())
      return null
    })
    .catch(_err => {
      console.error('There was error an updating experiment form field.', _err)
      return null
    })
  return response
}

export const submitExperiment = async (
  convId: string,
): Promise<(Conversation & { hasFormErrors?: boolean }) | null> => {
  const jwt = await getJwtToken()
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + jwt?.idToken,
    'Access-Token': jwt?.accessToken || '',
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/experiment_form/submit', {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      conversation_id: convId,
    }),
  })
    .then(async res => {
      if (res.ok) {
        return await res.json()
      } else if (res.status === 422) {
        const payload = await res.json()
        const response: Conversation = {
          id: payload.id,
          title: payload.title,
          messages: [],
          created_at: payload.created_at,
          extended_data: payload.extended_data,
          selected_agent: payload.selected_agent,
        }
        return { ...response, hasFormErrors: true }
      }
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const saveDraft = async (convId: string): Promise<(Conversation & { hasFormErrors?: boolean }) | null> => {
  const jwt = await getJwtToken()
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + jwt?.idToken,
    'Access-Token': jwt?.accessToken || '',
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/experiment_form/save_draft', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      conversation_id: convId,
    }),
  })
    .then(async res => {
      if (res.ok) {
        return await res.json()
      } else if (res.status === 422) {
        const payload = await res.json()
        const response: Conversation = {
          id: payload.id,
          title: payload.title,
          messages: [],
          created_at: payload.created_at,
          extended_data: payload.extended_data,
          selected_agent: payload.selected_agent,
        }
        return { ...response, hasFormErrors: true }
      }
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const experimentsList = async ({
  filters,
  search,
  projection,
  sortBy,
  sortOrder,
  page,
  pageSize,
}: ExperimentsListProps): Promise<ExperimentsListResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(
    `/api/experiments?filters=${encodeURIComponent(filters)}&projection=${encodeURIComponent(projection || '')}&sort_by=${encodeURIComponent(sortBy)}&sort_order=${sortOrder}&page=${page}&page_size=${pageSize}&search=${encodeURIComponent(search || '')}`,
    {
      method: 'GET',
      headers,
    },
  )
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      const payload = await res.json()
      return payload
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })
  return response
}

export const getExperimentFiltersOptions = async (): Promise<ExperimentFiltersOptions | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/experiments/filters', {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })
  return response
}

export const getExperimentIdByCode = async (experimentCode: string): Promise<string | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
  })

  const response = await fetchWithTokenRefresh(`/api/experiments/code/${encodeURIComponent(experimentCode)}`, {
    method: 'GET',
    headers,
  })

  if (!response || !response.ok) return null
  const data = await response.json()
  return data.id ?? null
}

export const listMicroLearnings = async ({
  search,
  status,
  sort_by,
  sort_order,
  page,
  per_page,
  ou,
  area,
  gla_strategic_priority,
  has_scale_potential,
}: MicroLearningsListParams): Promise<MicroLearningsListResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const queryParams = new URLSearchParams()
  if (search) queryParams.append('search', search)
  if (status && status !== 'all') queryParams.append('status', status)
  if (sort_by) queryParams.append('sort_by', sort_by)
  if (sort_order) queryParams.append('sort_order', sort_order)
  if (page) queryParams.append('page', page.toString())
  if (per_page) queryParams.append('per_page', per_page.toString())
  if (ou) queryParams.append('ou', ou)
  if (area) queryParams.append('area', area)
  if (gla_strategic_priority) queryParams.append('gla_strategic_priority', gla_strategic_priority)
  if (has_scale_potential !== undefined) {
    queryParams.append('has_scale_potential', has_scale_potential ? 'true' : 'false')
  }

  const queryString = queryParams.toString()
  const url = `/api/v1/micro-learnings${queryString ? `?${queryString}` : ''}`

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })
  return response
}

export const fetchMicroLearningFilters = async (): Promise<MicroLearningFilterOptions | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/v1/micro-learnings/filters', {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) return null
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching filter options.')
      return null
    })
  return response
}

export const getMicroLearningDetail = async (
  mlId: string,
  options?: { signal?: AbortSignal },
): Promise<{ data: MicroLearningDetailResponse | null; status: number | null }> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/v1/micro-learnings/${encodeURIComponent(mlId)}`, {
    method: 'GET',
    headers,
    signal: options?.signal,
  })
    .then(async res => {
      if (!res) {
        return { data: null, status: null }
      }
      if (!res.ok) {
        return { data: null, status: res.status }
      }
      return { data: (await res.json()) as MicroLearningDetailResponse, status: res.status }
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return { data: null, status: null }
    })

  return response
}

export const editMicroLearning = async (
  mlId: string,
  payload: EditMicroLearningPayload,
): Promise<EditMicroLearningResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/v1/micro-learnings/${mlId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue editing the micro-learning.')
      return null
    })
  return response
}

export const approveMicroLearning = async (
  mlId: string,
  publishedToOus: string[],
  note?: string,
): Promise<{ success: boolean; status: number | null }> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/v1/micro-learnings/${encodeURIComponent(mlId)}/approve`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      published_to_ous: publishedToOus,
      note: note || null,
    }),
  })
    .then(async res => {
      if (!res) {
        return { success: false, status: null }
      }
      if (!res.ok) {
        return { success: false, status: res.status }
      }
      return { success: true, status: res.status }
    })
    .catch(_err => {
      console.error('There was an issue approving the micro-learning.')
      return { success: false, status: null }
    })

  return response
}

export const getMicroLearningHistory = async (
  mlId: string,
  page = 1,
  pageSize = 20,
): Promise<MicroLearningHistoryEntry[]> => {
  const result = await apiFetch<MicroLearningHistoryEntry[]>(
    `/api/v1/micro-learnings/${encodeURIComponent(mlId)}/history?page=${page}&page_size=${pageSize}`,
  )
  return result ?? []
}

export const searchAvailableExperiments = async (
  search?: string,
  limit = 15,
): Promise<AvailableExperimentsResponse> => {
  const trimmedSearch = search?.trim()
  const query = trimmedSearch ? `search=${encodeURIComponent(trimmedSearch)}&limit=${limit}` : `limit=${limit}`
  let headers: Record<string, string>
  try {
    headers = await getApiHeaders({
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    })
  } catch (err) {
    console.error('There was an issue preparing the available experiments search request.', err)
    throw new Error('Could not load experiments. Please try again.')
  }

  const response = await fetchWithTokenRefresh(`/api/v1/micro-learnings/available-experiments?${query}`, {
    method: 'GET',
    headers,
  }).catch(err => {
    console.error('There was an issue searching available experiments.', err)
    throw new Error('Could not load experiments. Please try again.')
  })

  if (!response?.ok) {
    const body = (await response?.json().catch(() => null)) as { message?: string; error?: string } | null
    const message = body?.message ?? body?.error ?? 'Could not load experiments. Please try again.'
    throw new Error(message)
  }

  return (await response.json()) as AvailableExperimentsResponse
}

const readExperimentMutationBody = async (res: Response): Promise<{ message?: string; error?: string } | null> => {
  const raw = await res.text().catch(() => '')
  if (!raw) return null
  try {
    return JSON.parse(raw) as { message?: string; error?: string }
  } catch {
    return null
  }
}

const readExperimentMutationError = async (res: Response): Promise<string | undefined> => {
  const body = await readExperimentMutationBody(res)
  return body?.message ?? body?.error
}

export const addExperimentToMicroLearning = async (
  mlId: string,
  experimentCode: string,
  target: ExperimentLinkTarget,
): Promise<ExperimentMutationResult> => {
  if (!mlId?.trim() || !experimentCode?.trim()) {
    console.error('addExperimentToMicroLearning called with empty mlId or experimentCode.')
    return { success: false, status: null }
  }
  const normalizedMlId = mlId.trim()
  const normalizedExperimentCode = experimentCode.trim()

  let headers: Record<string, string>
  try {
    headers = await getApiHeaders({
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    })
  } catch (err) {
    console.error('There was an issue preparing the request to add the experiment.', err)
    return { success: false, status: null }
  }

  const payload: AddExperimentRequest = { experiment_code: normalizedExperimentCode }

  return fetchWithTokenRefresh(`/api/v1/micro-learnings/${encodeURIComponent(normalizedMlId)}/${target}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })
    .then(async res => {
      if (!res) {
        return { success: false, status: null }
      }
      if (!res.ok) {
        return { success: false, status: res.status, error: await readExperimentMutationError(res) }
      }
      const body = await readExperimentMutationBody(res)
      return { success: true, status: res.status, message: body?.message }
    })
    .catch(err => {
      console.error('There was an issue adding the experiment to the micro-learning.', err)
      return { success: false, status: null }
    })
}

export const removeExperimentFromMicroLearning = async (
  mlId: string,
  experimentCode: string,
  target: ExperimentLinkTarget,
): Promise<ExperimentMutationResult> => {
  if (!mlId?.trim() || !experimentCode?.trim()) {
    console.error('removeExperimentFromMicroLearning called with empty mlId or experimentCode.')
    return { success: false, status: null }
  }
  const normalizedMlId = mlId.trim()
  const normalizedExperimentCode = experimentCode.trim()

  let headers: Record<string, string>
  try {
    headers = await getApiHeaders({
      Authorization: 'Bearer ' + (await getIdToken()),
    })
  } catch (err) {
    console.error('There was an issue preparing the request to remove the experiment.', err)
    return { success: false, status: null }
  }

  return fetchWithTokenRefresh(
    `/api/v1/micro-learnings/${encodeURIComponent(normalizedMlId)}/${target}/${encodeURIComponent(normalizedExperimentCode)}`,
    {
      method: 'DELETE',
      headers,
    },
  )
    .then(async res => {
      if (!res) {
        return { success: false, status: null }
      }
      if (!res.ok) {
        return { success: false, status: res.status, error: await readExperimentMutationError(res) }
      }
      const body = await readExperimentMutationBody(res)
      return { success: true, status: res.status, message: body?.message }
    })
    .catch(err => {
      console.error('There was an issue removing the experiment from the micro-learning.', err)
      return { success: false, status: null }
    })
}

export const exportMicroLearning = async (
  mlId: string,
  format: MicroLearningExportFormat = 'xlsx',
): Promise<Response> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
  })

  return fetchWithTokenRefresh(`/api/v1/micro-learnings/${encodeURIComponent(mlId)}/export?format=${format}`, {
    method: 'POST',
    headers,
  })
}

export const reprocessMicroLearning = async (mlId: string): Promise<ExperimentMutationResult> => {
  if (!mlId?.trim()) {
    console.error('reprocessMicroLearning called with empty mlId.')
    return { success: false, status: null }
  }
  const normalizedMlId = mlId.trim()

  let headers: Record<string, string>
  try {
    headers = await getApiHeaders({
      Authorization: 'Bearer ' + (await getIdToken()),
    })
  } catch (err) {
    console.error('There was an issue preparing the request to reprocess the micro-learning.', err)
    return { success: false, status: null }
  }

  return fetchWithTokenRefresh(`/api/v1/micro-learnings/${encodeURIComponent(normalizedMlId)}/reprocess`, {
    method: 'POST',
    headers,
  })
    .then(async res => {
      if (!res) {
        return { success: false, status: null }
      }
      if (!res.ok) {
        return { success: false, status: res.status, error: await readExperimentMutationError(res) }
      }
      const body = await readExperimentMutationBody(res)
      return { success: true, status: res.status, message: body?.message }
    })
    .catch(err => {
      console.error('There was an issue reprocessing the micro-learning.', err)
      return { success: false, status: null }
    })
}

export const regenerateMicroLearning = async (
  mlId: string,
  feedback: { categories: string[]; text: string },
): Promise<{ success: boolean; status: number | null }> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/v1/micro-learnings/${encodeURIComponent(mlId)}/regenerate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ feedback }),
  })
    .then(async res => {
      if (!res) {
        return { success: false, status: null }
      }
      if (!res.ok) {
        return { success: false, status: res.status }
      }
      return { success: true, status: res.status }
    })
    .catch(_err => {
      console.error('There was an issue regenerating the micro-learning.')
      return { success: false, status: null }
    })

  return response
}

export const dismissMicroLearning = async (
  mlId: string,
  payload: DismissMicroLearningRequest,
): Promise<{ success: boolean; status: number | null }> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/v1/micro-learnings/${encodeURIComponent(mlId)}/dismiss`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })
    .then(async res => {
      if (!res) {
        return { success: false, status: null }
      }
      if (!res.ok) {
        return { success: false, status: res.status }
      }
      return { success: true, status: res.status }
    })
    .catch(_err => {
      console.error('There was an issue dismissing the micro-learning.')
      return { success: false, status: null }
    })

  return response
}

export const deleteExperiment = async (experimentId: string): Promise<Response> => {
  const jwt = await getJwtToken()

  const response = await fetchWithTokenRefresh('/api/experiment', {
    method: 'DELETE',
    headers: await getApiHeaders({
      Authorization: 'Bearer ' + jwt?.idToken,
      'Access-Token': jwt?.accessToken || '',
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({
      experiment_id: experimentId,
    }),
  })
  return response
}

export const expandCollapseExperimentForm = async (convId: string, expand: boolean): Promise<Conversation | null> => {
  const jwt = await getJwtToken()

  const response = await fetchWithTokenRefresh(`/api/experiment_form/${expand ? 'expand' : 'collapse'}`, {
    method: 'PUT',
    headers: await getApiHeaders({
      Authorization: 'Bearer ' + jwt?.idToken,
      'Access-Token': jwt?.accessToken || '',
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({
      conversation_id: convId,
    }),
  })
    .then(async res => {
      if (!res) {
        return null
      }
      const payload = await res.json()

      const conversation: Conversation = payload

      return conversation
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })
  return response
}

export const createExperimentForm = async (title: string): Promise<Conversation | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/experiment_form/create', {
    method: 'POST',
    headers,
    body: JSON.stringify({ title }),
  })
    .then(async res => {
      if (!res) {
        return null
      }
      const payload = await res.json()
      const conversation: Conversation = payload.conversation
      if (payload.messages) {
        conversation.messages = payload.messages.map((msg: any) => {
          return {
            id: msg.id,
            role: msg.role,
            date: msg.created_at,
            content: msg.content,
            feedback: msg.feedback ?? undefined,
            agent_name: msg.agent_name,
            sections: msg.sections,
            message_type: msg.message_type,
          }
        })
      }
      return conversation
    })
    .catch(_err => {
      console.error('There was an issue creating the experiment form.')
      return null
    })
  return response
}

export const createRoleChangeRequest = async (data: RoleChangeRequestPayload): Promise<RoleChangeRequestResponse> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/user/role-change-request', {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  })
    .then(async res => {
      const json = await res.json()

      if (!res.ok) {
        return {
          success: false,
          error: json.error || 'Failed to create role change request',
        }
      }

      return {
        success: true,
        request_id: json.request_id,
      }
    })
    .catch(err => {
      console.error('Error creating role change request:', err)
      return {
        success: false,
        error: 'Failed to create role change request. Please try again later.',
      }
    })

  return response
}

export const getRoleChangeRequestOptions = async (): Promise<RoleChangeRequestOptions | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })
  return await fetchWithTokenRefresh('/api/user/role-change-request/options', {
    method: 'GET',
    headers,
  })
    .then(async res => {
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })
}

export const getPendingRoleChangeRequests = async (
  page: number = 1,
  pageSize: number = 10,
  ou?: string,
): Promise<PendingRequestsResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  })

  if (ou) {
    params.append('ou', ou)
  }

  const response = await fetchWithTokenRefresh(`/api/admin/role-change-requests/pending?${params.toString()}`, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching pending role change requests.')
      return null
    })

  return response
}

export const approveRoleChangeRequest = async (requestId: string): Promise<Response | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/admin/role-change-requests/${requestId}/approve`, {
    method: 'POST',
    headers,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue approving the role change request.')
      return null
    })

  return response
}

export const declineRoleChangeRequest = async (requestId: string, notes?: string): Promise<Response | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/admin/role-change-requests/${requestId}/decline`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      notes: notes || null,
    }),
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue declining the role change request.')
      return null
    })

  return response
}

export const getRoleChangeRequestHistory = async (
  filters: RequestHistoryFilters = {},
): Promise<RequestHistoryResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  // Build query parameters
  const params = new URLSearchParams()
  if (filters.page) params.append('page', filters.page.toString())
  if (filters.page_size) params.append('page_size', filters.page_size.toString())
  if (filters.status) params.append('status', filters.status)
  if (filters.processed_by_user_id) params.append('processed_by_user_id', filters.processed_by_user_id)
  if (filters.start_date) params.append('start_date', filters.start_date)
  if (filters.end_date) params.append('end_date', filters.end_date)
  if (filters.sort_by) params.append('sort_by', filters.sort_by)
  if (filters.sort_order !== undefined) params.append('sort_order', filters.sort_order.toString())
  if (filters.ou) params.append('ou', filters.ou)

  const queryString = params.toString()
  const url = `/api/admin/role-change-requests/history${queryString ? `?${queryString}` : ''}`

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching role change request history.')
      return null
    })

  return response
}

export const getRoleChangeRequestFilterOptions = async (): Promise<FilterOptions | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/admin/role-change-requests/history/options', {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching filter options.')
      return null
    })

  return response
}

export const getNotifications = async (isRead?: boolean): Promise<NotificationsResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  let url = '/api/notifications'
  if (isRead !== undefined) {
    url += `?is_read=${isRead}`
  }

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching notifications.')
      return null
    })

  return response
}

export const markAllNotificationsAsRead = async (): Promise<Response | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/notifications/mark-all-read', {
    method: 'POST',
    headers,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue marking notifications as read.')
      return null
    })

  return response
}

export const deleteNotification = async (notificationId: string): Promise<Response | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/notifications/${notificationId}`, {
    method: 'DELETE',
    headers,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue deleting the notification.')
      return null
    })

  return response
}

export const getUnreadNotificationsCount = async (): Promise<UnreadNotificationsCountResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/notifications/unread-count', {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching unread notifications count.')
      return null
    })

  return response
}

// ==================== USER MANAGEMENT ENDPOINTS ====================

export const createUser = async (data: UserCreatePayload): Promise<UserResponse> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/admin/users', {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  })
    .then(async res => {
      const json = await res.json()

      if (!res.ok) {
        // Check if it's a validation error response
        if (json.errors && Array.isArray(json.errors)) {
          return {
            success: false,
            message: json.message || 'Validation failed',
            errors: json.errors,
          }
        }

        return {
          success: false,
          message: json.message || 'Failed to create user',
        }
      }

      return {
        success: true,
        user: json,
      }
    })
    .catch(err => {
      console.error('Error creating user:', err)
      return {
        success: false,
        message: 'Failed to create user. Please try again later.',
      }
    })

  return response
}

export const getUser = async (userId: string): Promise<UserResponse> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/admin/users/${userId}`, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      const json = await res.json()

      if (!res.ok) {
        return {
          success: false,
          message: json.message || 'Failed to get user',
        }
      }

      return {
        success: true,
        user: json,
      }
    })
    .catch(err => {
      console.error('Error getting user:', err)
      return {
        success: false,
        message: 'Failed to get user. Please try again later.',
      }
    })

  return response
}

export const updateUser = async (userId: string, data: UserUpdatePayload): Promise<UserResponse> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/admin/users/${userId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  })
    .then(async res => {
      const json = await res.json()

      if (!res.ok) {
        // Check if it's a validation error response
        if (json.errors && Array.isArray(json.errors)) {
          return {
            success: false,
            message: json.message || 'Validation failed',
            errors: json.errors,
          }
        }

        return {
          success: false,
          message: json.message || 'Failed to update user',
        }
      }

      return {
        success: true,
        user: json,
      }
    })
    .catch(err => {
      console.error('Error updating user:', err)
      return {
        success: false,
        message: 'Failed to update user. Please try again later.',
      }
    })

  return response
}

export const deleteUser = async (userId: string): Promise<UserDeleteResponse> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/admin/users/${userId}`, {
    method: 'DELETE',
    headers,
  })
    .then(async res => {
      const json = await res.json()

      if (!res.ok) {
        return {
          success: false,
          error: json.error || 'Failed to delete user',
        }
      }

      return {
        success: true,
        message: json.message || 'User deleted successfully',
      }
    })
    .catch(err => {
      console.error('Error deleting user:', err)
      return {
        success: false,
        error: 'Failed to delete user. Please try again later.',
      }
    })

  return response
}

export const listUsers = async (params?: UserListParams): Promise<UserListResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const queryParams = new URLSearchParams()
  if (params) {
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params.search) queryParams.append('search', params.search)

    // Add filters as JSON string
    if (params.filters && Object.keys(params.filters).length > 0) {
      queryParams.append('filters', JSON.stringify({ role: params.filters.role ?? [] }))
    }
  }

  const queryString = queryParams.toString()
  const url = `/api/admin/users${queryString ? `?${queryString}` : ''}`

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching users list.')
      return null
    })

  return response
}

export const getUserFilterOptions = async (): Promise<UserFilterOptions | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/admin/users/filter-options', {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching user filter options.')
      return null
    })

  return response
}

export const listUserHistory = async (params?: UserHistoryParams): Promise<UserHistoryResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const queryParams = new URLSearchParams()
  if (params) {
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params.search) queryParams.append('search', params.search)
    if (params.action_type) queryParams.append('action_type', params.action_type)
    if (params.processed_by_user_id) queryParams.append('processed_by_user_id', params.processed_by_user_id)
    if (params.start_date) queryParams.append('start_date', params.start_date)
    if (params.end_date) queryParams.append('end_date', params.end_date)
  }

  const queryString = queryParams.toString()
  const url = `/api/admin/users/history${queryString ? `?${queryString}` : ''}`

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching user history.')
      return null
    })

  return response
}

export const listExperimentLogs = async (params: ExperimentLogsParams): Promise<ExperimentLogsResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const experimentId = encodeURIComponent(params.experiment_id)
  const range = encodeURIComponent(params.range || 'all')

  const url = `/api/experiments/${experimentId}/history?range=${range}`

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching user history.')
      return null
    })

  return response
}

export const getExperimentLogsRanges = async (experimentId: string): Promise<ExperimentLogsRangesResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const experimentIdEncoded = encodeURIComponent(experimentId)

  const url = `/api/experiments/${experimentIdEncoded}/history/ranges`

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching experiment logs ranges.')
      return null
    })

  return response
}

export type ApplicationAnalyticsParams = {
  roles?: UserRole[]
  start_date?: string
  end_date?: string
}

export const getApplicationAnalytics = async (
  params?: ApplicationAnalyticsParams,
): Promise<ApplicationAnalyticsResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const queryParams = new URLSearchParams()
  params?.roles?.forEach(role => {
    queryParams.append('roles', role)
  })
  if (params?.start_date) {
    queryParams.append('start_date', params.start_date)
  }
  if (params?.end_date) {
    queryParams.append('end_date', params.end_date)
  }

  const queryString = queryParams.toString()
  const url = `/api/admin/analytics/application${queryString ? `?${queryString}` : ''}`

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        console.error('There was an issue fetching application analytics.')
        return null
      }
      return (await res.json()) as ApplicationAnalyticsResponse
    })
    .catch(err => {
      console.error('There was an issue fetching application analytics.', err)
      return null
    })

  return response
}

export const getAdoptionDashboardFilters = async (): Promise<AdoptionDashboardFiltersResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })
  const response = await fetch('/api/adoption-dashboard/periods', {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching adoption dashboard filters.')
      return null
    })

  return response
}

export const getAdoptionDashboardData = async (
  quarter: string,
  year: number,
): Promise<AdoptionDashboardDataResponse | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })
  const response = await fetch(
    `/api/adoption-dashboard?quarter=${encodeURIComponent(quarter)}&year=${encodeURIComponent(year)}`,
    {
      method: 'GET',
      headers,
    },
  )
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return await res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching adoption dashboard data.')
      return null
    })
  return response
}

export const getSummaryByOUPeriods = async (): Promise<PeriodItem[] | null> =>
  apiFetch<PeriodItem[]>('/api/adoption-dashboard/summary-by-ou/periods', {
    errorMessage: 'There was an issue fetching summary by OU periods.',
  })

export const getSummaryByOUFilters = async (
  year: number,
  quarter: string,
): Promise<SummaryByOUFiltersResponse | null> =>
  apiFetch<SummaryByOUFiltersResponse>(
    `/api/adoption-dashboard/summary-by-ou/filters?year=${encodeURIComponent(year)}&quarter=${encodeURIComponent(quarter)}`,
    { errorMessage: 'There was an issue fetching summary by OU filters.' },
  )

export const getSummaryByOU = async (
  year: number,
  quarter: string,
  title?: string,
  decodedLearning?: string,
): Promise<SummaryByOUResponse | null> => {
  let url = `/api/adoption-dashboard/summary-by-ou?year=${encodeURIComponent(year)}&quarter=${encodeURIComponent(quarter)}`
  if (title) {
    url += `&title=${encodeURIComponent(title)}`
  }
  if (decodedLearning) {
    url += `&decoded_learning=${encodeURIComponent(decodedLearning)}`
  }
  return apiFetch<SummaryByOUResponse>(url, {
    errorMessage: 'There was an issue fetching summary by OU data.',
  })
}

export const getViewEditAdoptionStatusList = async (
  year: number,
  quarter: string,
  filters?: {
    non_activated_markets?: boolean
    ou?: string
    mto_initiative?: string
    bpd?: string
    status?: string
  },
): Promise<ViewEditAdoptionStatusListResponse | null> => {
  let url = `/api/adoption-dashboard/view-edit-status?year=${encodeURIComponent(year)}&quarter=${encodeURIComponent(quarter)}`
  if (filters?.non_activated_markets !== undefined) {
    url += `&non_activated_markets=${filters.non_activated_markets}`
  }
  if (filters?.ou) {
    url += `&ou=${encodeURIComponent(filters.ou)}`
  }
  if (filters?.mto_initiative) {
    url += `&mto_initiative=${encodeURIComponent(filters.mto_initiative)}`
  }
  if (filters?.bpd) {
    url += `&bpd=${encodeURIComponent(filters.bpd)}`
  }
  if (filters?.status) {
    url += `&status=${encodeURIComponent(filters.status)}`
  }
  return apiFetch<ViewEditAdoptionStatusListResponse>(url, {
    errorMessage: 'There was an issue fetching view/edit adoption status list.',
  })
}

export const getViewEditAdoptionStatusFilters = async (
  year: number,
): Promise<ViewEditAdoptionStatusFiltersResponse | null> =>
  apiFetch<ViewEditAdoptionStatusFiltersResponse>(
    `/api/adoption-dashboard/view-edit-status/filters?year=${encodeURIComponent(year)}`,
    { errorMessage: 'There was an issue fetching view/edit adoption status filters.' },
  )

export const getViewEditAdoptionStatusById = async (id: string): Promise<ViewEditAdoptionStatusRow | null> =>
  apiFetch<ViewEditAdoptionStatusRow>(`/api/adoption-dashboard/view-edit-status/${encodeURIComponent(id)}`, {
    errorMessage: 'There was an issue fetching adoption status record.',
  })

export const updateViewEditAdoptionStatus = async (
  id: string,
  payload: {
    bpd_activation_status?: string
    indicate_tentative_month?: string
    brands?: string[]
    charters?: string[]
    blocker_comment?: string
  },
): Promise<unknown> => {
  const jwt = await getJwtToken()
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + jwt?.idToken,
    'Access-Token': jwt?.accessToken || '',
    'Content-Type': 'application/json',
  })
  const response = await fetch(`/api/adoption-dashboard/view-edit-status/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    throw new Error(response.status === 404 ? 'Record not found' : 'Failed to update adoption status')
  }
  return response.json().catch(() => ({}))
}

export const freezeViewEditAdoptionStatus = async (year: number): Promise<FreezeYTDResponse | null> =>
  apiFetch<FreezeYTDResponse>('/api/adoption-dashboard/view-edit-status/freeze', {
    method: 'POST',
    body: { year },
    errorMessage: 'There was an issue freezing adoption status data.',
  })

export type ConfirmOUStatusData = {
  sustainability: Record<string, boolean>
  other_learnings: Record<string, boolean>
}

export const getConfirmOUStatus = async (period: string): Promise<ConfirmOUStatusData> =>
  apiFetch<ConfirmOUStatusData>(`/api/adoption-dashboard/confirm-ou-status?period=${encodeURIComponent(period)}`, {
    errorMessage: 'There was an issue fetching confirm OU status.',
  }).then(res => res ?? { sustainability: {}, other_learnings: {} })

export const postConfirmOUStatus = async (
  period: string,
  sustainability: Record<string, boolean>,
  other_learnings: Record<string, boolean>,
): Promise<void> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })
  const response = await fetch('/api/adoption-dashboard/confirm-ou-status', {
    method: 'POST',
    headers,
    body: JSON.stringify({ period, sustainability, other_learnings }),
  })
  if (!response.ok) {
    throw new Error('Failed to save confirm OU status')
  }
}

export type SystemPromptMutationResult = {
  success: boolean
  prompt?: SystemPrompt
  message?: string
}

export const listSystemPrompts = async (): Promise<SystemPrompt[] | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh('/api/admin/system-prompts', {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return (await res.json()) as SystemPrompt[]
    })
    .catch(_err => {
      console.error('There was an issue fetching system prompts.')
      return null
    })

  return response
}

export const getSystemPrompt = async (key: string): Promise<SystemPrompt | null> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/admin/system-prompts/${encodeURIComponent(key)}`, {
    method: 'GET',
    headers,
  })
    .then(async res => {
      if (!res || !res.ok) {
        return null
      }
      return (await res.json()) as SystemPrompt
    })
    .catch(_err => {
      console.error('There was an issue fetching the system prompt.')
      return null
    })

  return response
}

export const updateSystemPrompt = async (key: string, content: string): Promise<SystemPromptMutationResult> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/admin/system-prompts/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ content }),
  })
    .then(async res => {
      const json = await res.json().catch(() => ({}))

      if (!res.ok) {
        return {
          success: false,
          message: (json as Record<string, string>).message || 'Failed to update system prompt',
        }
      }

      return {
        success: true,
        prompt: json as SystemPrompt,
      }
    })
    .catch(err => {
      console.error('Error updating system prompt:', err)
      return {
        success: false,
        message: 'Failed to update system prompt. Please try again later.',
      }
    })

  return response
}

export const resetSystemPrompt = async (key: string): Promise<SystemPromptMutationResult> => {
  const headers = await getApiHeaders({
    Authorization: 'Bearer ' + (await getIdToken()),
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTokenRefresh(`/api/admin/system-prompts/${encodeURIComponent(key)}/reset`, {
    method: 'POST',
    headers,
  })
    .then(async res => {
      const json = await res.json().catch(() => ({}))

      if (!res.ok) {
        return {
          success: false,
          message: (json as Record<string, string>).message || 'Failed to reset system prompt',
        }
      }

      return {
        success: true,
        prompt: json as SystemPrompt,
      }
    })
    .catch(err => {
      console.error('Error resetting system prompt:', err)
      return {
        success: false,
        message: 'Failed to reset system prompt. Please try again later.',
      }
    })

  return response
}
